import { AlertInlineVariants } from "@tvg/design-system/web/components/AlertInLine/types";

import { IconNames } from "@tvg/design-system/web";

type PromoType = "retention" | "acquisition";

export type Platform = "ios" | "web";

export type Segment = "acq" | "ret" | "all";

export type LinkType = "story" | "url";

export enum TabEligibleRacesEnum {
  COMING_RACES = "comingRaces",
  FUTURE_RACES = "futureRaces"
}

export type Path = {
  id?: string;
  url: string;
  cached_url: string;
  linktype: LinkType;
};

export type Promo = {
  _uid?: string;
  promoType?: PromoType;
  tagTitle?: string;
  categoryTitle?: string;
  categoryTitleColor?: string;
  buttonLabel?: string;
  ctaLabel?: string;
  title: string;
  description: string;
  descriptionColor?: string;
  displayCTA?: boolean;
  forOptin?: boolean;
  path: Path;
  optinStatus?: boolean;
  pictureAlt?: string;
  pictureUrl?: string;
  mobilePictureAlt?: string;
  mobilePictureUrl?: string;
  isTalentImg?: boolean;
  segment?: Segment;
  isTopPromo?: boolean;
  promoCode?: string;
  promoId?: number;
  backgroundColor?: string;
  isOptedIn?: boolean;
  pictureHeight?: number;
};

type BannerConfig = {
  _uid?: string;
  path: Path;
  plugin?: "banner_plugin";
  segment: Segment;
  ctaLabel: string;
  forOptin: boolean;
  promoId: string;
  promoCode: string;
  displayCTA: boolean;
  linkLabel: string;
  enablePromoCode?: boolean;
};

export type BannerItem = {
  _uid?: string;
  config: BannerConfig;
  categoryTitle: string;
  title: string;
  description: string;
  component?: "banner";
  pictureAlt?: string;
  pictureUrl?: string;
  mobilePictureUrl?: string;
  backgroundColor?: string;
  categoryTitleColor?: string;
  descriptionColor?: string;
};

export type Assets = {
  pictureAlt: string;
  pictureUrl: string;
  isTalentImg: boolean;
};

export type RichTextContent = {
  content: Object[];
  type: string;
};

export type Seo = {
  title: string;
  description: string | RichTextContent;
};

export type InfoStep = {
  id?: number;
  iconName?: string;
  stepNumber: string;
  stepDescription?: string;
};

export type IconType =
  | "depositIcon"
  | "videoIcon"
  | "watchIcon"
  | "rewardsIcon";

export type StepLink = {
  _uid?: string;
  component?: string;
  icon: IconType;
  text: string;
  link: Path;
};

export type StoreLinks = {
  brand: string;
  appleStore: string;
  androidStore: string;
};

export type Info = {
  promoStepItems?: InfoStep[];
  appStoreLinks: StoreLinks;
  showTitle: boolean;
  showInfoSteps: boolean;
  showAdditionalContent: boolean;
  title?: string;
  additionalContent?: string;
};

export type LandingPage = {
  bannerItem: Promo;
  bannerAssets: Assets;
  primarySlotItem: Promo;
  secondarySlotItem: Promo;
  seoItem: Seo;
  promoSlotItems: Promo[];
  legalItem: string;
  segmentType: Segment;
  showSeoSection: boolean;
  showLegalsSection: boolean;
};

export type PromoPage = {
  bannerItem: Promo;
  bannerAssets: Assets;
  bodyItems: Promo[];
  helpItem: Info;
  seoItem: Seo;
  legalItem: string;
  showHelpSection: boolean;
  showSeoSection: boolean;
};

export type Blok = {
  component: string;
  _uid: string;
  config: Config;
  segment?: Segment;
  slots?: Promo[];
  promoCode?: string;
  promoId?: number;
  header?: string;
  linkLabel?: string;
  path?: Path;
  date?: Date;
  bloks?: Blok[];
  title?: string;
  description?: string;
};

export type RichTextParagraph = {
  text: string;
  type: string;
};
export type attrsContent = {
  body: Blok[];
  id: string;
};

export type RichText = {
  type: string;
  content: RichTextParagraph;
};

export type RichTextInlineComp = {
  type: string;
  attrs: attrsContent;
};

export type ContentProps = {
  body: Blok[];
  component: string;
  _uid: string;
};

export type PageProps = {
  data: {
    story: ContentProps;
  };
};

export type OptinPromoStatus = {
  id: number;
  optedIn: boolean;
  path: string;
};

export type UserOptedInPromos = {
  [key: string]: boolean;
};

export type ContextPath = {
  currentSlug: string;
  setContent: (newSlug: string) => void;
  redirectUserToExternalSite: (url: string) => void;
};

export type User = {
  isLogged: boolean;
  accountNumber: string;
  returningUser: boolean;
  optedInPromos: UserOptedInPromos;
  homeState: string;
};

export type Error =
  | ""
  | "PromotionNotFoundException"
  | "PromotionNotAvailableForOptin"
  | "NotEligibleException"
  | "UserOnBlackListException"
  | "NotInValidOptinRangeException"
  | "InternalError"
  | "Error";

export type Optin = {
  isOpted: boolean;
  optedIn: boolean;
  error: Error;
  handleOptinClick: () => void;
};

export type TableBody = {
  firstCellText: string;
  secondCellText: string;
  thirdCellText?: string;
  fourthCellText?: string;
  firstCellTextColor: string;
  secondCellTextColor?: string;
  thirdCellTextColor?: string;
  fourthCellTextColor?: string;
};

export type TableProps = {
  title: string;
  description: string;
  tableContent: Array<Blok & TableBody>;
};

export type BlokType = {
  component: string;
  _uid: string;
};

export type Card = {
  categoryTitle: string;
  title: string;
  description: string;
  pictureAlt: string;
  pictureUrl: string;
  pictureHeight: number;
  path: Path;
  promoId: number;
  promoCode: string;
};

export type PromoCard = {
  BlokType: BlokType;
  Card: Card;
};

export interface Config {
  ctaLabel: string;
  linkLabel: string;
  path: Path;
  segment: Segment;
  promoCode: string;
  displayCTA: boolean;
  forOptin: boolean;
  plugin?: string;
  promoId: string;
  enablePromoCode: boolean;
}

export interface LinkProps {
  path: string;
  url: string;
  tag: string;
}

export interface PromosContent {
  bloks?: Blok[];
  ctaLabel?: string;
  forOptin: boolean;
  linkLabel: string;
  path: Path;
  isForBanner: boolean;
  segment?: Segment;
  promoCode?: string;
}

interface OptedInMessage {
  text: string;
  variant: AlertInlineVariants;
}

export interface OptedInMessages {
  success: {
    successOptedIn: OptedInMessage;
    alreadyOptedIn: OptedInMessage;
  };
  error: {
    default: OptedInMessage;
  };
  info: {
    invalidOptinRange: OptedInMessage;
    notFound: OptedInMessage;
    startDate: OptedInMessage;
  };
  warning: {
    notEligible: OptedInMessage;
    userOnBlackList: OptedInMessage;
  };
}

export interface ErrorMessages {
  PromotionNotFoundException: {
    warning: {
      notEligible: OptedInMessage;
    };
  };
  NotEligibleException: {
    warning: {
      notEligible: OptedInMessage;
    };
  };
  UserOnBlackListException: {
    warning: {
      userOnBlackList: OptedInMessage;
    };
  };
  NotInValidOptinRangeException: {
    info: {
      invalidOptinRange: OptedInMessage;
    };
  };
  PromotionNotAvailableForOptin: {
    error: {
      notFound: OptedInMessage;
    };
  };
  InternalError: {
    error: {
      internalError: OptedInMessage;
    };
  };
  default: {
    error: {
      default: OptedInMessage;
    };
  };
}

interface HandicappingSectionItem {
  title: string;
  description: string;
  iconLeft: IconNames;
  iconRight: IconNames;
  path: string;
}

export interface HandicappingSectionItems {
  title: string;
  items: HandicappingSectionItem[];
}
